import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  getTutorialSpeechUrl,
  isTutorialMuted,
  setTutorialMuted,
} from "./tutorialSpeech";

// Visual language mirrors the Smart Search / Sequence Search tutorial so the
// experience is consistent across the app.
const _isLightTheme =
  typeof window !== "undefined" && localStorage.getItem("theme") === "light";

const ACCENT = _isLightTheme ? "#2363F7" : "#BEFF00";
const CARD_BG = _isLightTheme
  ? "#ffffff"
  : "linear-gradient(150deg, #1e2e14, #111a0b)";
const TITLE_COLOR = _isLightTheme ? "#0f1117" : "#ffffff";
const BODY_COLOR = _isLightTheme ? "#6B7280" : "#8a9e80";
const SKIP_COLOR = _isLightTheme ? "#9CA3AF" : "#5a6e50";
const BTN_TEXT = _isLightTheme ? "#ffffff" : "#0a0e05";
const STEP_INACTIVE = _isLightTheme ? "#E5E7EB" : "rgba(255,255,255,0.12)";
const FONT_BASE = '"Satoshi Medium", "Satoshi-Regular", sans-serif';
const FONT_BOLD = '"SatoshiBold", "Satoshi-Bold", sans-serif';
const OVERLAY_Z = 100050;
const CARD_Z = 100060;

const SPOTLIGHT_PADDING = 10;
const CARD_WIDTH = 340;
const CARD_GAP = SPOTLIGHT_PADDING + 16;
const VIEWPORT_MARGIN = 12;

const MuteIcon = ({ muted }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 5 6 9H2v6h4l5 4V5z" fill="currentColor" stroke="none" />
    {muted ? (
      <>
        <line x1="22" y1="9" x2="16" y2="15" />
        <line x1="16" y1="9" x2="22" y2="15" />
      </>
    ) : (
      <>
        <path d="M15.5 8.5a5 5 0 0 1 0 7" />
        <path d="M18.5 5.5a9 9 0 0 1 0 13" />
      </>
    )}
  </svg>
);

const measure = (selector) => {
  if (typeof document === "undefined") {
    return null;
  }
  const element = document.querySelector(selector);
  if (!element) {
    return null;
  }
  return { element, rect: element.getBoundingClientRect() };
};

/**
 * Spotlight tour styled to match the Smart Search tutorial: an SVG-mask dim
 * overlay with a rounded cutout around the current step's target (resolved by
 * CSS selector), a pulsing accent ring, and an explanatory card with progress
 * segments and Skip / Next navigation. Steps whose target is missing on a page
 * are skipped so the same list can be reused across pages.
 */
const SpotlightTour = ({ steps, open, onClose }) => {
  const { t } = useTranslation();
  const [activeSteps, setActiveSteps] = useState([]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [muted, setMuted] = useState(isTutorialMuted);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setActiveSteps(steps.filter((step) => measure(step.selector) !== null));
    setIndex(0);
  }, [open, steps]);

  const current = activeSteps[index];

  // Single advance path shared by the Next button, the Enter key, and the
  // end of the (unmuted) narration: move to the next step, or finish the
  // tour on the last one.
  const handleAdvance = useCallback(() => {
    if (index >= activeSteps.length - 1) {
      onClose();
    } else {
      setIndex((value) => value + 1);
    }
  }, [activeSteps.length, index, onClose]);

  // Keep a ref to the latest advance handler so the audio effect can call it on
  // narration end without re-subscribing (and reloading audio) every step.
  const advanceRef = useRef(null);
  useEffect(() => {
    advanceRef.current = handleAdvance;
  });

  const updateRect = useCallback(() => {
    if (!current) {
      return;
    }
    const found = measure(current.selector);
    setRect(found ? found.rect : null);
  }, [current]);

  useLayoutEffect(() => {
    if (!open || !current) {
      return undefined;
    }
    const found = measure(current.selector);
    if (typeof found?.element?.scrollIntoView === "function") {
      try {
        found.element.scrollIntoView({ block: "center", behavior: "smooth" });
      } catch (e) {
        /* scrollIntoView is a no-op in some environments (e.g. jsdom) */
      }
    }
    const raf = requestAnimationFrame(updateRect);
    const timeout = setTimeout(updateRect, 320);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [open, current, updateRect]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [open, updateRect]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "Enter") {
        // Enter immediately jumps to the next step (or finishes the tour).
        event.preventDefault();
        handleAdvance();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, handleAdvance]);

  // Read the current card aloud (cached TTS), unless muted. Autoplay may be
  // blocked until the user interacts (e.g. clicks Next or the mute button) —
  // that rejection is swallowed and playback resumes on the next step.
  useEffect(() => {
    const audio = audioRef.current;
    if (!open || !current || muted) {
      audio?.pause();
      return undefined;
    }

    let cancelled = false;
    const text = `${current.title}. ${current.body}`;

    getTutorialSpeechUrl(text)
      .then((url) => {
        if (cancelled) {
          return;
        }
        if (!audioRef.current) {
          audioRef.current = new Audio();
        }
        const player = audioRef.current;
        player.src = url;
        // When narration is audible (not muted), advance automatically as soon
        // as it finishes. Muted runs return early above, so no auto-advance.
        player.onended = () => {
          advanceRef.current?.();
        };
        player.play().catch(() => {});
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      const player = audioRef.current;
      if (player) {
        player.pause();
        player.onended = null;
      }
    };
  }, [open, current, muted]);

  const toggleMute = () => {
    setMuted((previous) => {
      const next = !previous;
      setTutorialMuted(next);
      return next;
    });
  };

  if (!open || !current) {
    return null;
  }

  const isLast = index === activeSteps.length - 1;

  // Clamp the highlight box to the viewport so the padding/glow ring around a
  // target pinned to an edge (e.g. the analyst-mode toggle at right: 2px) is not
  // clipped off-screen.
  const spotlightBox = computeSpotlightBox(rect);

  const { cardStyle, arrowSide } = computeCardPlacement(rect);

  return (
    <>
      {rect ? (
        <svg
          style={{
            position: "fixed",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "auto",
            zIndex: OVERLAY_Z,
          }}
          onClick={onClose}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <mask id="events-tutorial-spotlight-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={spotlightBox.left}
                y={spotlightBox.top}
                width={spotlightBox.width}
                height={spotlightBox.height}
                rx="12"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.78)"
            mask="url(#events-tutorial-spotlight-mask)"
          />
        </svg>
      ) : (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.82)",
            zIndex: OVERLAY_Z,
            pointerEvents: "auto",
          }}
        />
      )}

      {spotlightBox ? (
        <div
          style={{
            position: "fixed",
            left: spotlightBox.left,
            top: spotlightBox.top,
            width: spotlightBox.width,
            height: spotlightBox.height,
            borderRadius: 14,
            border: `2px solid ${ACCENT}`,
            boxShadow: `0 0 0 1px ${ACCENT}44, 0 0 24px ${ACCENT}66, 0 0 48px ${ACCENT}22`,
            zIndex: OVERLAY_Z + 1,
            pointerEvents: "none",
            animation: "eventsTutorialPulse 2s ease-in-out infinite",
          }}
        />
      ) : null}

      <div
        key={current.selector}
        style={{
          ...cardStyle,
          zIndex: CARD_Z,
          background: CARD_BG,
          border: _isLightTheme
            ? "1px solid #E5E7EB"
            : `1.5px solid ${ACCENT}66`,
          borderRadius: 20,
          padding: "26px 26px 20px",
          boxShadow: _isLightTheme
            ? "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)"
            : `0 32px 80px rgba(0,0,0,0.88), 0 0 0 1px ${ACCENT}22, 0 0 40px ${ACCENT}18, inset 0 1px 0 ${ACCENT}22`,
          fontFamily: FONT_BASE,
          userSelect: "none",
          WebkitUserSelect: "none",
        }}
      >
        {arrowSide === "top" ? (
          <div
            style={{
              position: "absolute",
              top: -9,
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "9px solid transparent",
              borderRight: "9px solid transparent",
              borderBottom: `9px solid ${ACCENT}88`,
              filter: `drop-shadow(0 -2px 4px ${ACCENT}44)`,
            }}
          />
        ) : arrowSide === "bottom" ? (
          <div
            style={{
              position: "absolute",
              bottom: -9,
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "9px solid transparent",
              borderRight: "9px solid transparent",
              borderTop: `9px solid ${ACCENT}88`,
              filter: `drop-shadow(0 2px 4px ${ACCENT}44)`,
            }}
          />
        ) : null}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 14,
          }}
        >
          {activeSteps.map((step, stepIndex) => (
            <div
              key={step.selector}
              style={{
                height: 3,
                flex: 1,
                borderRadius: 99,
                background: stepIndex <= index ? ACCENT : STEP_INACTIVE,
                transition: "background 0.4s",
                boxShadow: stepIndex <= index ? `0 0 6px ${ACCENT}88` : "none",
              }}
            />
          ))}
          <span
            style={{
              color: _isLightTheme ? ACCENT : `${ACCENT}99`,
              flexShrink: 0,
              fontFamily: FONT_BASE,
              fontSize: 10,
              fontWeight: 700,
              marginLeft: 2,
            }}
          >
            {index + 1}/{activeSteps.length}
          </span>
          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? "Unmute narration" : "Mute narration"}
            title={muted ? "Unmute narration" : "Mute narration"}
            style={{
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 26,
              height: 26,
              marginLeft: 4,
              padding: 0,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: ACCENT,
            }}
          >
            <MuteIcon muted={muted} />
          </button>
        </div>

        <h2
          style={{
            margin: "0 0 10px",
            fontSize: 18,
            fontWeight: 800,
            fontFamily: FONT_BOLD,
            color: TITLE_COLOR,
            lineHeight: 1.25,
          }}
        >
          {current.title}
        </h2>

        <p
          style={{
            margin: "0 0 24px",
            fontSize: 13.5,
            fontFamily: FONT_BASE,
            color: BODY_COLOR,
            lineHeight: 1.6,
          }}
        >
          {current.body}
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: SKIP_COLOR,
              fontSize: 13,
              cursor: "pointer",
              padding: "6px 0",
              fontFamily: FONT_BASE,
              fontWeight: 500,
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            {t("tutorial.skip")}
          </button>

          <button
            type="button"
            onClick={handleAdvance}
            style={{
              background: ACCENT,
              border: "none",
              color: BTN_TEXT,
              fontFamily: FONT_BOLD,
              fontWeight: 800,
              fontSize: 13.5,
              padding: "10px 24px",
              borderRadius: 10,
              cursor: "pointer",
              boxShadow: `0 4px 20px ${ACCENT}55`,
            }}
          >
            {isLast ? t("tutorial.done") : t("tutorial.next")}
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes eventsTutorialPulse {
            0%,100% { box-shadow: 0 0 0 1px ${ACCENT}44, 0 0 20px ${ACCENT}55, 0 0 40px ${ACCENT}22; }
            50% { box-shadow: 0 0 0 2px ${ACCENT}88, 0 0 32px ${ACCENT}99, 0 0 64px ${ACCENT}44; }
          }
        `}
      </style>
    </>
  );
};

const computeSpotlightBox = (rect) => {
  if (!rect) {
    return null;
  }
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1024;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : 768;

  const left = Math.max(VIEWPORT_MARGIN, rect.left - SPOTLIGHT_PADDING);
  const top = Math.max(VIEWPORT_MARGIN, rect.top - SPOTLIGHT_PADDING);
  const right = Math.min(
    viewportWidth - VIEWPORT_MARGIN,
    rect.right + SPOTLIGHT_PADDING
  );
  const bottom = Math.min(
    viewportHeight - VIEWPORT_MARGIN,
    rect.bottom + SPOTLIGHT_PADDING
  );

  return {
    left,
    top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
};

const computeCardPlacement = (rect) => {
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1024;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : 768;
  const estimatedHeight = 210;

  if (!rect) {
    return {
      cardStyle: {
        position: "fixed",
        top: viewportHeight / 2 - estimatedHeight / 2,
        left: viewportWidth / 2 - CARD_WIDTH / 2,
        width: CARD_WIDTH,
      },
      arrowSide: null,
    };
  }

  let left = rect.left + rect.width / 2 - CARD_WIDTH / 2;
  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, viewportWidth - CARD_WIDTH - VIEWPORT_MARGIN)
  );

  const spaceBelow = viewportHeight - rect.bottom - CARD_GAP - VIEWPORT_MARGIN;
  const placeBelow = spaceBelow >= estimatedHeight || spaceBelow > rect.top;

  let top;
  let arrowSide;
  if (placeBelow) {
    top = rect.bottom + CARD_GAP;
    arrowSide = "top";
  } else {
    top = Math.max(VIEWPORT_MARGIN, rect.top - CARD_GAP - estimatedHeight);
    arrowSide = "bottom";
  }

  return {
    cardStyle: { position: "fixed", top, left, width: CARD_WIDTH },
    arrowSide,
  };
};

export default SpotlightTour;
